/**
 * User Management Panel
 * Admin interface for managing user accounts
 * Phase 2: Multi-User Auth Foundation
 */

import React, { useState } from 'react';
import { useUsers } from '../../../hooks/domain/useUsers';
import { useCustomDialogs } from '../../../hooks/useCustomDialogs';
import { validatePasswordStrength } from '../../../platform/auth/passwordUtils';
// FT-103: Import to detect Azure mode for password field behavior
import { isAzureApiConfigured } from '../../../lib/azureApiClient';
import {
  Users,
  UserPlus,
  Edit2,
  Key,
  Ban,
  RefreshCw,
  Shield,
  Anchor,
  Users as CrewIcon,
  AlertCircle,
  Check,
  X,
  Loader2,
  Eye,
  EyeOff,
  Copy,
  CheckCircle
} from 'lucide-react';

// FT-98: Role display configuration using RBAC role values
// FT-100: Added super_admin for display (platform-level, not assignable but must display correctly)
const ROLE_CONFIG = {
  super_admin: { label: 'Super Admin', icon: Shield, color: 'text-red-600 bg-red-100' },
  fleet: { label: 'Fleet Manager / Owner', icon: Shield, color: 'text-purple-600 bg-purple-100' },
  skipper: { label: 'Officer / Skipper', icon: Anchor, color: 'text-blue-600 bg-blue-100' },
  crew: { label: 'Crew Member', icon: CrewIcon, color: 'text-gray-600 bg-gray-100' }
};

// Available roles for assignment (super_admin excluded - platform role only)
const ASSIGNABLE_ROLES = ['crew', 'skipper', 'fleet'];

// FT-103: Temp password display modal
const TempPasswordModal = ({ userName, userEmail, tempPassword, onClose }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(tempPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = tempPassword;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">User Created Successfully</h2>
              <p className="text-sm text-gray-600">Share these credentials securely</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* User details */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Name:</span>
              <span className="text-sm font-medium text-gray-900">{userName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Email:</span>
              <span className="text-sm font-medium text-gray-900">{userEmail}</span>
            </div>
          </div>

          {/* Temporary password */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm font-medium text-amber-800 mb-2">Temporary Password</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-white border border-amber-300 rounded px-3 py-2 font-mono text-lg text-gray-900 select-all">
                {tempPassword}
              </code>
              <button
                onClick={handleCopy}
                className="p-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                title="Copy password"
              >
                {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-xs text-amber-700 mt-2">
              ⚠️ This password expires in 7 days. The user must change it on first login.
            </p>
          </div>

          {/* Instructions */}
          <div className="text-sm text-gray-600">
            <p className="font-medium mb-1">Next steps:</p>
            <ol className="list-decimal list-inside space-y-1 text-gray-500">
              <li>Share the email and password securely with the user</li>
              <li>The user logs in at your organization's SMS URL</li>
              <li>They'll be prompted to set a new password</li>
            </ol>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

// User form modal
// FT-103: isAzureMode prop controls password field visibility
const UserFormModal = ({ user, onSave, onClose, title, isAzureMode = false }) => {
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    jobTitle: user?.jobTitle || '',  // FT-98: Job title (free text, e.g. "Chief Engineer")
    role: user?.role || 'crew',      // FT-98: Default to crew role
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const isEdit = !!user;
  // FT-103: In Azure mode, password is generated server-side (not needed in form)
  const showPasswordFields = !isAzureMode || isEdit;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }
    // FT-103: Skip ALL password validation in Azure mode for new users (API generates temp password)
    const requiresPassword = !isAzureMode && !isEdit;
    if (requiresPassword && !formData.password) {
      setError('Password is required');
      return;
    }
    // Only validate password if one was entered (edit mode or dev mode)
    if (formData.password) {
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      const strength = validatePasswordStrength(formData.password);
      if (!strength.valid) {
        setError(strength.errors[0]);
        return;
      }
    }

    setLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="John Smith"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="john@vessel.com"
            />
          </div>

          {/* FT-98: Job Title (optional free text) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Job Title <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={formData.jobTitle}
              onChange={(e) => handleChange('jobTitle', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g. Chief Engineer, Deck Officer, Bosun"
            />
          </div>

          {/* FT-98: Permission Role (required) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Permission Role
            </label>
            <select
              value={formData.role}
              onChange={(e) => handleChange('role', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {ASSIGNABLE_ROLES.map(roleKey => (
                <option key={roleKey} value={roleKey}>{ROLE_CONFIG[roleKey]?.label || roleKey}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Determines sign-off and management permissions
            </p>
          </div>

          {/* FT-103: Password fields - hidden in Azure mode for new users (API generates temp password) */}
          {showPasswordFields && (
            <>
              {/* Password (required for new in dev mode, optional for edit) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {isEdit ? 'New Password (leave blank to keep current)' : 'Password'}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {formData.password && (
                  <div className="mt-2 text-xs text-gray-500 space-y-1">
                    <p className={formData.password.length >= 8 ? 'text-green-600' : ''}>
                      {formData.password.length >= 8 ? '✓' : '○'} At least 8 characters
                    </p>
                    <p className={/[A-Z]/.test(formData.password) ? 'text-green-600' : ''}>
                      {/[A-Z]/.test(formData.password) ? '✓' : '○'} One uppercase letter
                    </p>
                    <p className={/[0-9]/.test(formData.password) ? 'text-green-600' : ''}>
                      {/[0-9]/.test(formData.password) ? '✓' : '○'} One number
                    </p>
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              {formData.password && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password
                  </label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => handleChange('confirmPassword', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="••••••••"
                  />
                </div>
              )}
            </>
          )}

          {/* FT-103: Info message when Azure mode (password generated server-side) */}
          {isAzureMode && !isEdit && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> A temporary password will be generated automatically.
                You'll need to share it with the user after creation.
              </p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {isEdit ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Password reset modal
const ResetPasswordModal = ({ user, onReset, onClose }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!password) {
      setError('Password is required');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const strength = validatePasswordStrength(password);
    if (!strength.valid) {
      setError(strength.errors[0]);
      return;
    }

    setLoading(true);
    try {
      await onReset(user.id, password);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Reset Password</h2>
          <p className="text-sm text-gray-600 mt-1">Set a new password for {user.name}</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
              Reset Password
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const UserManagementPanel = () => {
  const {
    users,
    loading,
    error,
    isAdmin,
    addUser,
    updateUser,
    deactivateUser,
    reactivateUser,
    resetPassword,
    refresh,
    ROLES
  } = useUsers();

  const { showConfirm, showAlert, ConfirmDialog, AlertDialog } = useCustomDialogs();

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  // FT-103: State for temp password display modal
  const [tempPasswordInfo, setTempPasswordInfo] = useState(null);

  // FT-103: Check if Azure API is configured (affects form behavior)
  const isAzureMode = isAzureApiConfigured();

  // Not admin - show access denied
  if (!isAdmin) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Access Restricted</h3>
        <p className="text-gray-600">Only administrators can manage user accounts.</p>
      </div>
    );
  }

  // FT-98: Handle add user (includes jobTitle)
  // FT-103: Capture and display temp password from Azure API response
  const handleAddUser = async (formData) => {
    const result = await addUser(
      {
        name: formData.name,
        email: formData.email,
        jobTitle: formData.jobTitle || null,  // Optional field
        role: formData.role
      },
      formData.password // Ignored by Azure API, used in dev mode
    );

    // FT-103: Show temp password modal if Azure API returned one
    if (result?.tempPassword) {
      setTempPasswordInfo({
        name: formData.name,
        email: formData.email,
        tempPassword: result.tempPassword
      });
    }
  };

  // FT-98: Handle edit user (includes jobTitle)
  const handleEditUser = async (formData) => {
    const updates = {
      name: formData.name,
      email: formData.email,
      jobTitle: formData.jobTitle || null,
      role: formData.role
    };
    // FT-395 (Cluster B): updateUser is now async (Azure API in prod). Await so a
    // rejection propagates to UserFormModal.handleSubmit's try/catch (surfaced as
    // an inline error) instead of becoming an unhandled promise rejection, and so
    // the password reset runs only after the update settles.
    await updateUser(editingUser.id, updates);
    if (formData.password) {
      await resetPassword(editingUser.id, formData.password);
    }
  };

  // Handle deactivate
  const handleDeactivate = async (user) => {
    // FT-100: Prevent self-deactivation (useUsers already blocks this, but UI feedback is better)
    if (user.isSelf) {
      return; // Can't deactivate yourself
    }

    const confirmed = await showConfirm(
      'Deactivate User',
      `Are you sure you want to deactivate ${user.name}? They will no longer be able to sign in.`,
      'Deactivate',
      'Cancel',
      'warning'
    );

    if (confirmed) {
      // FT-395 (Cluster B): deactivateUser is now async (Azure API in prod). Await
      // and surface any error (e.g. last-admin guard, network) via an alert instead
      // of an unhandled promise rejection.
      try {
        await deactivateUser(user.id);
      } catch (err) {
        showAlert('Deactivation failed', err.message || 'Could not deactivate user', 'error');
      }
    }
  };

  // Handle reactivate
  const handleReactivate = async (user) => {
    // FT-395 (Cluster B): reactivateUser is now async (Azure API in prod). Await
    // and surface any error via an alert instead of an unhandled promise rejection.
    try {
      await reactivateUser(user.id);
    } catch (err) {
      showAlert('Reactivation failed', err.message || 'Could not reactivate user', 'error');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6 text-gray-700" />
          <h2 className="text-lg font-semibold text-gray-900">User Management</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <UserPlus className="w-4 h-4" />
            Add User
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <Loader2 className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading users...</p>
        </div>
      ) : (
        /* User List */
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map(user => {
                // FT-98: Handle both old (ADMIN/MASTER) and new (fleet/skipper) role values
                const normalizedRole = user.role?.toLowerCase();
                const roleConfig = ROLE_CONFIG[normalizedRole] || ROLE_CONFIG.crew;
                const RoleIcon = roleConfig?.icon || CrewIcon;

                return (
                  <tr key={user.id} className={!user.isActive ? 'bg-gray-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                        {/* FT-98: Show job title if present */}
                        {user.jobTitle && (
                          <div className="text-sm text-cyan-600">{user.jobTitle}</div>
                        )}
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${roleConfig?.color || 'text-gray-600 bg-gray-100'}`}>
                        <RoleIcon className="w-3 h-3" />
                        {roleConfig?.label || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.isActive ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <Check className="w-3 h-3" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          <X className="w-3 h-3" />
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.lastLogin
                        ? new Date(user.lastLogin).toLocaleDateString()
                        : 'Never'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      {/* FT-100: Disable actions for self (use Profile tab instead) */}
                      {user.isSelf ? (
                        <span className="text-xs text-gray-400 italic">Use Profile tab</span>
                      ) : (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setEditingUser(user)}
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setResetUser(user)}
                            className="p-1.5 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded"
                            title="Reset Password"
                          >
                            <Key className="w-4 h-4" />
                          </button>
                          {user.isActive ? (
                            <button
                              onClick={() => handleDeactivate(user)}
                              className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                              title="Deactivate"
                            >
                              <Ban className="w-4 h-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleReactivate(user)}
                              className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded"
                              title="Reactivate"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {showAddModal && (
        <UserFormModal
          title="Add New User"
          onSave={handleAddUser}
          onClose={() => setShowAddModal(false)}
          isAzureMode={isAzureMode}
        />
      )}

      {editingUser && (
        <UserFormModal
          user={editingUser}
          title="Edit User"
          onSave={handleEditUser}
          onClose={() => setEditingUser(null)}
          isAzureMode={isAzureMode}
        />
      )}

      {resetUser && (
        <ResetPasswordModal
          user={resetUser}
          onReset={resetPassword}
          onClose={() => setResetUser(null)}
        />
      )}

      {/* FT-103: Temp password display modal */}
      {tempPasswordInfo && (
        <TempPasswordModal
          userName={tempPasswordInfo.name}
          userEmail={tempPasswordInfo.email}
          tempPassword={tempPasswordInfo.tempPassword}
          onClose={() => setTempPasswordInfo(null)}
        />
      )}

      <ConfirmDialog />
      <AlertDialog />
    </div>
  );
};

export default UserManagementPanel;
